import "./scrollable.css";
import { Page } from "@nike/epic-react-ui";
import { TableRef } from "../components/base-table";
import React, { useState, useReducer, useEffect, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import type { SortingState, ColumnFiltersState, ColumnOrderState, RowSelectionState, VisibilityState, PaginationState as TanstackPaginationState } from "@tanstack/react-table";
import { Exception } from "../graphql/graphql";
import { ExceptionsTable } from "../components/exceptions-table/ExceptionsTable";
import { getTableColumns } from "../components/exceptions-table/ExceptionTableColumns";
import { ExceptionDetail } from "../components/exception-detail/ExceptionDetail";
import { useQuery, useApolloClient } from '@apollo/client';
import { User } from "../graphql/graphql";
import "./exceptions-page.scss";
import { GET_EXCEPTIONS, GET_EXCEPTION_TYPES, LIST_EXCEPTION_COUNTRIES, GET_EXCEPTION } from '../graphql/exceptions.graphql';
import type { TableColumn } from "../components/exceptions-table/ExceptionTableColumns";
import { useExceptionHandlers } from "../components/exception-detail/useExceptionHandlers";
import { encodeFilters, decodeFilters } from "../shared/Utils";
import { PageSpinner } from "../shared/PageSpinner";
import { GqlErrorModal } from "../shared/GqlErrorModal";
import { equals } from "ramda";
import { Text } from "@nike/eds";
import { TableUISettings, UISettings, DEFAULT_UI_SETTINGS } from "../shared/Types";
import { UserUISettingsService } from "../services/UserUISettingsService"
import { LIST_USERS } from "../graphql/users.graphql";

// Extend PaginationState to include totalCount
type PaginationState = TanstackPaginationState & {
  totalCount?: number;
};

type ExceptionPageError = {
  message: string;
  code?: string;
  details?: any;
};

type ExceptionsPageState = {
    loading: boolean;
    error: ExceptionPageError | null;
    data: any;
    configurationIsOpen: boolean;
    sorting: SortingState;
    columnOrder: ColumnOrderState;
    columnVisibility: VisibilityState;
    columnFilters: ColumnFiltersState;
    viewingException?: Exception | null;
    detailIsOpen: boolean;
    rowSelection: RowSelectionState;
    pagination: PaginationState & {
      totalCount?: number;
    };
    globalSearch?: string; 
    exceptionTypes: { value: string; label: string }[];
    importCountriesOptions: { value: string; label: string }[];
    originCountriesOptions: { value: string; label: string }[];
    assigneeOptions: { value: string; label: string }[];
    columnFiltersSidePanelIsOpen?: boolean;
};

type ExceptionsPageAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: any }
  | { type: "FETCH_ERROR"; payload: ExceptionPageError }
  | { type: "OPEN_CONFIGURATION"}
  | { type: "CLOSE_CONFIGURATION"}
  | { type: "SET_SORTING"; payload: SortingState }
  | { type: "SET_COLUMN_ORDER"; payload: ColumnOrderState }
  | { type: "SET_COLUMN_VISIBILITY"; payload: VisibilityState }
  | { type: "SET_COLUMN_FILTERS"; payload: ColumnFiltersState }
  | { type: "SET_VIEWING_EXCEPTION"; payload: Exception | null | undefined }
  | { type: "OPEN_DETAIL"}
  | { type: "CLOSE_DETAIL"}
  | { type: "SET_ROW_SELECTION"; payload: RowSelectionState }
  | { type: "SET_PAGINATION"; payload: PaginationState }
  | { type: "SET_GLOBAL_FILTER"; payload: string }
  | { type: "SET_EXCEPTION_TYPES"; payload: { value: string; label: string }[] }
  | { type: "SET_IMPORT_COUNTRIES_OPTIONS"; payload: { value: string; label: string }[] }
  | { type: "SET_ORIGIN_COUNTRIES_OPTIONS"; payload: { value: string; label: string }[] }
  | { type: "SET_URGENCY_OPTIONS"; payload: { value: string; label: string }[] }
  | { type: "OPEN_COLUMN_FILTERS_SIDE_PANEL" }
  | { type: "CLOSE_COLUMN_FILTERS_SIDE_PANEL" }
  | { type: "RESET_ROW_SELECTION" }
  | { type: "SET_ASSIGNEE_OPTIONS"; payload: { value: string; label: string }[] };

// Helper to set all columns visible based on ExceptionTableColumns
function getAllColumnsVisible(): Record<string, boolean> {
  // getTableColumns returns an array of column definitions, each with an 'id'
  return getTableColumns().reduce((acc, col) => {
    if (col.accessorKey !== undefined) {
      acc[col.accessorKey] = true;
    }
    return acc;
  }, {} as Record<string, boolean>);
}

function getInitialColumnOrder(): ColumnOrderState {
  return getTableColumns()
    .map(col => col.accessorKey)
    .filter((id): id is string => typeof id === "string");
}

// Helper to translate sorting state to orderBy argument for useQuery
function getOrderByFromSorting(
  sorting: { id: string; desc: boolean }[],
  columns: ReturnType<typeof getTableColumns>
) {
  if (!sorting.length) return undefined;
  const sort = sorting[0];
  const col = columns.find(c => c.accessorKey === sort.id);
  if (!col || !col.sortableField) return undefined;
  return {
    field: col.sortableField,
    direction: sort.desc ? "DESC" : "ASC",
  };
}

function exceptionsPageReducer(state: ExceptionsPageState, action: ExceptionsPageAction): ExceptionsPageState {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return { ...state, loading: false, error: null, data: action.payload };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "OPEN_CONFIGURATION":
      return { ...state, configurationIsOpen: true };
    case "CLOSE_CONFIGURATION":
      return { ...state, configurationIsOpen: false };
    case "SET_EXCEPTION_TYPES":
      return { ...state, exceptionTypes: [...action.payload] };
    case "SET_SORTING":
      return { ...state, sorting: action.payload };
    case "SET_COLUMN_ORDER":
      return { ...state, columnOrder: action.payload };
    case "SET_COLUMN_VISIBILITY":
      return { ...state, columnVisibility: action.payload };
    case "SET_COLUMN_FILTERS": 
      return { 
        ...state, 
        columnFilters: [...action.payload],
        pagination: { 
          ...state.pagination, 
          pageIndex: 0 // reset to first page when filters change
        }
      };
    case "SET_IMPORT_COUNTRIES_OPTIONS":
      return { ...state, importCountriesOptions: [...action.payload] };
    case "SET_ORIGIN_COUNTRIES_OPTIONS":
      return { ...state, originCountriesOptions: [...action.payload] };
    case "SET_ASSIGNEE_OPTIONS":
      return { ...state, assigneeOptions: [...action.payload] };
    case "SET_VIEWING_EXCEPTION":
      return { ...state, viewingException: action.payload };
    case "OPEN_DETAIL":
        return { ...state, detailIsOpen: true };
    case "CLOSE_DETAIL":
      return { ...state, detailIsOpen: false, viewingException: null };
    case "SET_ROW_SELECTION":
      return { ...state, rowSelection: action.payload };
    case "RESET_ROW_SELECTION":
      return { ...state, rowSelection: {} };
    case "SET_PAGINATION":
      return { ...state, pagination: { ...state.pagination, ...action.payload } };
    case "SET_GLOBAL_FILTER":
      return { ...state, globalSearch: action.payload };
    case "OPEN_COLUMN_FILTERS_SIDE_PANEL":
      return { ...state, columnFiltersSidePanelIsOpen: true };
    case "CLOSE_COLUMN_FILTERS_SIDE_PANEL":
      return { ...state, columnFiltersSidePanelIsOpen: false };
    default:
      return state;
  }
}

type ExceptionsProps = {
  history: any;
  // globalSearch: string;
  currentTerritories: string[];
  currentUser?: User | undefined;
};

export function Exceptions({ history, currentTerritories, currentUser }: ExceptionsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = useParams<{ id?: string }>();
  const [showErrorModal, setShowErrorModal] = useState(true);

  const skipQuery = !currentTerritories || currentTerritories.length === 0;
  const territoryVars = React.useMemo(() => ({ customsTerritories: currentTerritories }), [currentTerritories]);
  const navigate = useNavigate();

  const client = useApolloClient();
  // Detect E2E test runtime (set via page.addInitScript in Playwright)
  const isE2E = typeof window !== 'undefined' && (window as any).__E2E__ === true;
  // In E2E mode, prefer localStorage-backed settings to avoid noisy network-only queries / remount churn
  const settingsService = useMemo(() => new UserUISettingsService(client, isE2E), [client, isE2E]);
  const [settings, setSettings] = useState<UISettings | undefined>(undefined);

  const settingsKey = "exceptions";
  // const tableSettings: TableUISettings | undefined = settings?.tables?.[settingsKey];

  // Parse uiSettings if it exists and is a string
  const parsedUISettings: UISettings | undefined = (() => {
    const raw = currentUser?.settings?.uiSettings;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw) as UISettings;
      } catch (e) {
        console.error("Failed to parse uiSettings:", raw, e);
        return undefined;
      }
    }
    return raw as UISettings | undefined;
  })();

  const tableSettings: TableUISettings | undefined = 
  {
    pageSize: parsedUISettings?.[`${settingsKey}PageSize`] as number || 10,
    columnOrder: parsedUISettings?.[`${settingsKey}ColumnOrder`] as string[] || getInitialColumnOrder(),
    columnVisibility: parsedUISettings?.[`${settingsKey}ColumnVisibility`] as Record<string, boolean> || getAllColumnsVisible(),
  };

  useEffect(() => {
    // Skip remote settings fetch in E2E to reduce GraphQL chatter & DOM re-renders
    if (isE2E) {
      try {
        const existing = localStorage.getItem('uiSettings');
        if (existing) {
          setSettings(JSON.parse(existing));
        } else {
          // Seed a minimal settings blob so pageSize persistence logic works without mutation round-trip
          const seed = { consignmentsBannerDismissed: 'false' } as any;
          localStorage.setItem('uiSettings', JSON.stringify(seed));
          setSettings(seed);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[E2E] Failed to parse or seed uiSettings', e);
      }
      return;
    }
    let mounted = true;
    settingsService.getSettings().then(result => {
      if (mounted) setSettings(result);
    }).catch(err => {
      if (mounted) console.error('Failed to load settings:', err);
    });
    return () => { mounted = false; };
  }, [settingsService, isE2E]);

  // Load exception types - NO onCompleted
  const { data: exceptionTypesData } = useQuery(GET_EXCEPTION_TYPES, {
    variables: territoryVars,
    skip: skipQuery,
    fetchPolicy: 'cache-first',
  });

  // Load import countries - NO onCompleted
  const { data: importCountriesData } = useQuery(LIST_EXCEPTION_COUNTRIES, {
    variables: {
      fieldType: 'import', customsTerritories: currentTerritories
    },
    fetchPolicy: 'cache-first',
    skip: skipQuery,
  });

  // Load origin countries - NO onCompleted
  const { data: originCountriesData } = useQuery(LIST_EXCEPTION_COUNTRIES, {
    variables: {
      fieldType: 'origin', customsTerritories: currentTerritories
    },
    fetchPolicy: 'cache-first',
    skip: skipQuery,
  });

  // Load users - NO onCompleted
  const { data: usersData } = useQuery(LIST_USERS, {
    variables: { limit: 1000, customsTerritories: currentTerritories || [] },
    skip: skipQuery,
  });

  // Initialize columnFilters from URL
  const initialColumnFilters = React.useMemo(
    () => decodeFilters(searchParams.get("filters")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const initialGlobalSearch = React.useMemo(
    () => searchParams.get("tableSearch") || "",
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [state, dispatch] = useReducer(exceptionsPageReducer, {
    loading: false,
    error: null,
    data: null,
    configurationIsOpen: false,
    sorting: [],
    columnOrder: getInitialColumnOrder(),
    columnVisibility: getAllColumnsVisible(),
    columnFilters: initialColumnFilters,
    columnFiltersSidePanelIsOpen: false,
    viewingException: null,
    detailIsOpen: false,
    rowSelection: {}, 
    pagination: {
      pageIndex: 0,
      pageSize: tableSettings?.pageSize || 10, // Default page size
      totalCount: undefined, // This will be updated by the query response
    },
    exceptionTypes: [],
    importCountriesOptions: [],
    originCountriesOptions: [],
    assigneeOptions: [],
    globalSearch: initialGlobalSearch,
  });

    // Process exception types data in useEffect
  useEffect(() => {
    if (exceptionTypesData?.exception?.types) {
      const types = exceptionTypesData.exception.types;
      const newOptions = types.map((t: any) => ({
        value: t.code,
        label: t.description,
      }));
      dispatch({
        type: "SET_EXCEPTION_TYPES",
        payload: newOptions,
      });
    }
  }, [exceptionTypesData]);

  // Process import countries data in useEffect
  useEffect(() => {
    if (importCountriesData?.exception?.listCountries) {
      const countries = importCountriesData.exception.listCountries;
      const newOptions = countries.map((c: string) => ({
        value: c,
        label: c,
      }));
      dispatch({
        type: "SET_IMPORT_COUNTRIES_OPTIONS",
        payload: newOptions,
      });
    }
  }, [importCountriesData]);

  // Process origin countries data in useEffect
  useEffect(() => {
    if (originCountriesData?.exception?.listCountries) {
      const countries = originCountriesData.exception.listCountries;
      const newOptions = countries.map((c: string) => ({
        value: c,
        label: c,
      }));
      dispatch({
        type: "SET_ORIGIN_COUNTRIES_OPTIONS",
        payload: newOptions,
      });
    }
  }, [originCountriesData]);

  // Process users data in useEffect
  useEffect(() => {
    if (usersData?.user?.list?.items) {
      const assigneeOptions =
        usersData.user.list.items
          .slice()
          .sort((a: User, b: User) => a.name.localeCompare(b.name))
          .map((user: User) => ({
            label: user.name,
            value: user.id,
          }));
      
      dispatch({
        type: "SET_ASSIGNEE_OPTIONS",
        payload: assigneeOptions,
      });
    }
  }, [usersData]);

  React.useEffect(() => {
    // Whenever filters in the URL change, update reducer state
    const decoded = decodeFilters(searchParams.get("filters"));
    if (!equals(decoded, state.columnFilters)) {
      dispatch({ type: "SET_COLUMN_FILTERS", payload: decoded });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("filters")]);

  // Sync columnFilters and globalSearch to URL when they change
  React.useEffect(() => {
    const encodedFilters = encodeFilters(state.columnFilters);
    const urlGlobalSearch = searchParams.get("tableSearch") || "";
    let shouldUpdate = false;

    // Handle filters param: remove if empty, set if not
    if (encodedFilters !== searchParams.get("filters")) {
      if (encodedFilters) {
        searchParams.set("filters", encodedFilters);
      } else {
        searchParams.delete("filters");
      }
      shouldUpdate = true;
    }

    if ((state.globalSearch || "") !== urlGlobalSearch) {
      if (state.globalSearch) {
        searchParams.set("tableSearch", state.globalSearch);
      } else {
        searchParams.delete("tableSearch");
      }
      shouldUpdate = true;
    }
    if (shouldUpdate) {
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.columnFilters, state.globalSearch]);

  // Memoize columnsDef so it is stable across renders
  const columnsDef = React.useMemo(() => getTableColumns(), []);
  const orderBy = getOrderByFromSorting(state.sorting, columnsDef);

  /**
   * Translates columnFilters array to a filter object usable by the query.
   * Example: [{id: 'status', value: 'OPEN'}] => { status: 'OPEN' }
   */
  function columnFiltersToQueryFilter(
    columnFilters: ColumnFiltersState,
    columns: TableColumn<Exception>[],
    globalSearch?: string
  ): Record<string, any> | null {
    const filter: Record<string, any> = {};
    columnFilters.forEach(({ id, value }) => {
      // Find the columnDef for this filter
      const col = columns.find(c => c.accessorKey === id || c.id === id);
      const filterKey = col?.filterKey || id;

    if (
      typeof value === 'object' &&
      value !== null &&
      (Object.prototype.hasOwnProperty.call(value, 'from') || Object.prototype.hasOwnProperty.call(value, 'to'))
    ) {
        const from = (value as { from?: string }).from;
        const to = (value as { to?: string }).to;
        filter[`${filterKey}From`] = from || undefined;
        filter[`${filterKey}To`] = to || undefined;
      } else {
        filter[filterKey] = value;
      }
    });

    if (globalSearch) {
      filter.searchTerm = globalSearch;
    }
    const hasValidFilter = Object.values(filter).some(
      v => v !== undefined && v !== null && v !== ''
    );

    return hasValidFilter ? filter : null;
  }

  const queryFilter = React.useMemo(() => columnFiltersToQueryFilter(state.columnFilters, columnsDef, state.globalSearch),
      [state.columnFilters, columnsDef, state.globalSearch]
  );
  // Only run the list query if no id is present
  const shouldSkipListQuery = !!id || !currentTerritories || currentTerritories.length === 0 || !currentUser;
  const shouldSkipSingleQuery = !id;

  const { loading, error, data, refetch } = useQuery(GET_EXCEPTIONS, {
    skip: shouldSkipListQuery,
    variables: {
      limit: state.pagination.pageSize, 
      offset: state.pagination.pageIndex * state.pagination.pageSize, 
      orderBy: orderBy,
      filter: queryFilter,
      customsTerritories: currentTerritories,
    },
    pollInterval: 30000,
  });

  useEffect(() => {
    if (!data) return;
    const pageInfo = data?.exception?.list?.pageInfo;
    const items = data?.exception?.list?.items;
    if (pageInfo?.status === "ERROR" || items == null) {
      dispatch({
        type: "FETCH_ERROR",
        payload: { message: pageInfo?.message || "Unknown error fetching exceptions." },
      });
      return;
    }
    dispatch({
      type: "FETCH_SUCCESS",
      payload: {
        ...data,
        totalCount: pageInfo?.totalCount,
      },
    });
    if (state.pagination.totalCount !== pageInfo?.totalCount) {
      dispatch({
        type: "SET_PAGINATION",
        payload: {
          ...state.pagination,
          totalCount: pageInfo?.totalCount,
        },
      });
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  // Only depend on data; dispatch is stable from useReducer and does not need to be included

  useEffect(() => {
    if (error) {
      dispatch({ type: "FETCH_ERROR", payload: { message: error.message } });
    }
  }, [error]);

  // Synchronize Apollo loading with reducer loading - only dispatch when transitioning to loading
  useEffect(() => {
    if (loading && !state.loading) {
      dispatch({ type: "FETCH_START" });
    }
  }, [loading, state.loading]);


  const handleCloseDetail = React.useCallback(() => {
    if (id) {
      navigate("/exceptions", { replace: true });
      setTimeout(() => {
          dispatch({ type: "SET_VIEWING_EXCEPTION", payload: null });
          dispatch({ type: "RESET_ROW_SELECTION" });
          dispatch({ type: "CLOSE_DETAIL" });
      }, 100);
    } else {
      dispatch({ type: "CLOSE_DETAIL" });
      dispatch({ type: "SET_VIEWING_EXCEPTION", payload: null });
      dispatch({ type: "RESET_ROW_SELECTION" });
      
    }
  }, [id, navigate]);
  

  // Query for a single exception if direct navigation and rows are empty
  const { data: singleExceptionData, loading: singleExceptionLoading } = useQuery(GET_EXCEPTION, {
    variables: { id },
    skip: shouldSkipSingleQuery, // Only run if id is present and rows are empty
  });
  
  React.useEffect(() => {
    if (id && singleExceptionData?.exception?.get) {
      // Direct navigation: set the single exception as the only row
      dispatch({
        type: "FETCH_SUCCESS",
        payload: {
          exception: {
            list: {
              items: [singleExceptionData.exception.get],
              pageInfo: { totalCount: 1 }
            }
          }
        }
      });
      dispatch({ type: "SET_ROW_SELECTION", payload: { [id]: true } });
      dispatch({ type: "SET_VIEWING_EXCEPTION", payload: singleExceptionData.exception.get });
      dispatch({ type: "OPEN_DETAIL" });
      (window as any).__EXCEPTIONS_DETAIL_OPEN__ = true;
    }
  }, [id, singleExceptionData, dispatch]);

  const handleRowSelectionChange = (newRowSelection: RowSelectionState) => {
    const exceptionId = Object.keys(newRowSelection)[0];
    const exception: Exception | null | undefined= rows.find((ex) => ex.id === exceptionId);
    dispatch({ type: "SET_ROW_SELECTION", payload: newRowSelection });
    dispatch({ type: "SET_VIEWING_EXCEPTION", payload: exception });
    dispatch({ type: "OPEN_DETAIL" });
    (window as any).__EXCEPTIONS_DETAIL_OPEN__ = true;
  };

  const tableRef = React.useRef<TableRef>(null);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && state.detailIsOpen) {
        // handleCloseDetail();
        dispatch({ type: "CLOSE_DETAIL" });
      }
      if ((event.key === "Escape") && state.columnFiltersSidePanelIsOpen) {
        dispatch({ type: "CLOSE_COLUMN_FILTERS_SIDE_PANEL" });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [state.detailIsOpen, state.columnFiltersSidePanelIsOpen, handleCloseDetail]);


  React.useEffect(() => {
    if (state.detailIsOpen && state.columnFiltersSidePanelIsOpen) {
      dispatch({ type: "CLOSE_COLUMN_FILTERS_SIDE_PANEL" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.detailIsOpen]);

  const {
    assignToMeHandler,
    assignToUserHandler,
    retryHandler,
    markAsOpenHandler,
    markInProgressHandler,
    assignLoading,
    retryLoading,
    inProgressLoading,
    openLoading,
    assignError,
    retryError,
    inProgressError,
    openError
  } = useExceptionHandlers(state.viewingException, currentUser, refetch);

  // Table rows: only from list query if no id, otherwise from single query
  const rows: Exception[] = React.useMemo(() => {
    return id ? 
      singleExceptionData?.exception?.get ? [singleExceptionData.exception.get] : [] : 
      data?.exception?.list?.items || [];
  }, [id, singleExceptionData?.exception?.get, data?.exception?.list?.items]);

  // Expose readiness flag for E2E tests once at least one exception row is available
  React.useEffect(() => {
    if (rows && rows.length > 0) {
      (window as any).__EXCEPTIONS_READY__ = true;
    }
  }, [rows]);

  // Only show ExceptionDetail if id is present or detailIsOpen
  const exceptionId = id || state.viewingException?.id || null;

  React.useEffect(() => {
    if (!id || rows.length === 0) return;

    const exception = rows.find((ex) => ex.id === id);
    if (!exception) return;

    // Only update if not already selected/viewing
    const alreadySelected = state.rowSelection[id];
    const alreadyViewing = state.viewingException?.id === id;
    const detailIsOpen = state.detailIsOpen;

    if (!alreadySelected || !alreadyViewing || !detailIsOpen) {
      dispatch({ type: "SET_ROW_SELECTION", payload: { [id]: true } });
      dispatch({ type: "SET_VIEWING_EXCEPTION", payload: exception });
      dispatch({ type: "OPEN_DETAIL" });
      (window as any).__EXCEPTIONS_DETAIL_OPEN__ = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, rows, state.rowSelection, state.viewingException, state.detailIsOpen]);
  

  React.useEffect(() => {
    if (assignError) {
      dispatch({ 
        type: "FETCH_ERROR", 
        payload: { 
          message: assignError.message || "Assignment failed",
          code: "ASSIGNMENT_ERROR",
          details: assignError 
        } 
      });
    }
  }, [assignError]);

  const isTableLoading = React.useMemo(() => {
    if (shouldSkipListQuery && shouldSkipSingleQuery) return false;
    if (shouldSkipListQuery) return singleExceptionLoading;
    return loading || state.loading;
  }, [shouldSkipListQuery, shouldSkipSingleQuery, singleExceptionLoading, loading, state.loading]);

  return (
    <Page documentTitle="Exceptions - Customs Compliance Hub" width="full" className="scrollable exceptions-page">
      
        {!currentUser ?<PageSpinner />
         : 
         <>
          {exceptionId && (
          <ExceptionDetail
            isOpen={state.detailIsOpen}
            onClose={handleCloseDetail}
            exceptionId={exceptionId}
            currentUser={currentUser}
            assignToMeHandler={assignToMeHandler}
            assignToUserHandler={assignToUserHandler}
            retryHandler={retryHandler}
            markAsOpenHandler={markAsOpenHandler}
            markInProgressHandler={markInProgressHandler}
            assignLoading={assignLoading}
            retryLoading={retryLoading}
            inProgressLoading={inProgressLoading}
            openLoading={openLoading}
            assignError={assignError}
            retryError={retryError}
            inProgressError={inProgressError}
            openError={openError}
          />
          )}
          <ExceptionsTable
            tableRef={tableRef}
            columns={() => getTableColumns()}
            data={state.loading ? [] : rows}
            tableTitle={<Text font="title-2" as="h2">Exceptions</Text>}
            getRowId={(originalRow: Exception) => originalRow.id}
            state={{ 
                sorting: state.sorting, 
                columnFilters: state.columnFilters, 
                setSorting: (newSorting: SortingState) => dispatch({ type: "SET_SORTING", payload: newSorting }), 
                setColumnFilters: (newFilters: ColumnFiltersState) => dispatch({ type: "SET_COLUMN_FILTERS", payload: newFilters }),
                columnOrder: state.columnOrder,
                setColumnOrder: (newOrder: string[]) => dispatch({ type: "SET_COLUMN_ORDER", payload: newOrder }),
                setRowSelection: handleRowSelectionChange,
                rowSelection: state.rowSelection,
                resetRowSelection: () => dispatch({ type: "RESET_ROW_SELECTION" }),
                columnVisibility: state.columnVisibility,
                setColumnVisibility: (newVisibility: VisibilityState) => dispatch({ type: "SET_COLUMN_VISIBILITY", payload: newVisibility }),
                pagination: {
                  totalCount: state.pagination.totalCount
                },
                globalSearch: state.globalSearch,
                columnFiltersSidePanelIsOpen: state.columnFiltersSidePanelIsOpen,
            }}
            onPaginationChange={(pagination) => {
                dispatch({ type: "SET_PAGINATION", payload: pagination });
                const newSettings = settingsService.setTablePageSize(
                  settings || DEFAULT_UI_SETTINGS,
                  settingsKey,
                  pagination.pageSize
                );
                settingsService.changeSettings(newSettings).then(setSettings);

            }}
            pageIndex={state.pagination.pageIndex}
            pageSize={state.pagination.pageSize}
            totalCount={state.pagination.totalCount}
            isLoading={isTableLoading}
            error={state.error}
            enableGlobalFilter={!shouldSkipListQuery}
            enableColumnFiltersSidePanel={!shouldSkipListQuery}
            onGlobalFilterChange={(filter) => {
              // Update global filter in state and URL
              dispatch({ type: "SET_GLOBAL_FILTER", payload: filter });
              // The effect above will sync it to the URL
            }}
            meta={{ 
              exceptionTypeOptions: state.exceptionTypes, 
              importCountriesOptions: state.importCountriesOptions, 
              originCountriesOptions: state.originCountriesOptions,
              assigneeOptions: state.assigneeOptions,
            }}
            onColumnFiltersSidePanelOpenChange={(isOpen) => {
              if (isOpen) {
                dispatch({ type: "OPEN_COLUMN_FILTERS_SIDE_PANEL" });
              } else {
                dispatch({ type: "CLOSE_COLUMN_FILTERS_SIDE_PANEL" });
              }
            }}
          />
         </>
        }

      {showErrorModal && error && (
        <GqlErrorModal error={error} onClose={() => setShowErrorModal(false)} />
      )}
    </Page>
  );
}
